'use client';
import React, { useState, useRef, useEffect } from 'react';
import { IconMap } from '../IconMap';
import { Button } from '../Buttons';
import { Icon } from '../Icon';

export interface DropdownButtonOption {
  label: string;
  value: string;
  icon?: keyof typeof IconMap;
  onClick?: () => void;
}

export interface DropdownButtonProps {
  buttonLabel: string;
  showPrefixIcon?: boolean;
  options: DropdownButtonOption[];
  onOptionClick?: (value: string) => void;
  showSuffixIcon?: boolean;
  suffixIcon?: React.ReactNode;
  prefixIcon?: React.ReactNode;
}

export function DropdownButton({
  buttonLabel,
  showPrefixIcon,
  options,
  onOptionClick,
  showSuffixIcon = true,
  suffixIcon,
  prefixIcon,
}: DropdownButtonProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative inline-block" ref={ref}>
      <Button
        variant="primary"
        className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded shadow hover:bg-pink-700 transition focus:outline-none focus:ring-2 focus:ring-pink-400"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        {showPrefixIcon && <span>{prefixIcon}</span>}
        <span>{buttonLabel}</span>
        {showSuffixIcon && (suffixIcon || <Icon name="ArrowWhite" size={20} />)}
      </Button>
      {open && (
        <ul className="absolute z-20 mt-2 w-96 bg-white border border-secondary-200 rounded shadow-lg" role="menu">
          {options.map((opt) => (
            <li
              key={opt.value}
              className="flex items-center px-3 py-2 cursor-pointer select-none transition hover:bg-secondary-100"
              role="menuitem"
              onClick={() => {
                opt.onClick?.();
                onOptionClick?.(opt.value);
                setOpen(false);
              }}
            >
              {opt.icon && <Icon name={opt.icon} size={20} />}
              <span className="flex-1 truncate">{opt.label}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
